import { useState, type JSX } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Modal, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import type { AxiosError } from "axios";

import NoDataFound from "src/components/common/NoDataFound";
import {
  getRecycleBinDashboardsApi,
  permanentlyDeleteDashboardApi,
  restoreDashboardApi,
} from "src/api/dashboardApi";
import type {
  RecycleBinDashboardItem,
  RecycleBinDashboardsResponse,
} from "src/types/dashboard";

export default function RecycleBinPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Confirmation gate for the irreversible permanent-delete action — holds
  // the item pending confirmation, or null when the dialog is closed.
  const [pendingDelete, setPendingDelete] =
    useState<RecycleBinDashboardItem | null>(null);

  const { data: recycleBin, isLoading } = useQuery<
    RecycleBinDashboardsResponse,
    Error
  >({
    queryKey: ["dashboards", "recycle-bin"],
    queryFn: () => getRecycleBinDashboardsApi(),
    staleTime: 0,
  });

  const {
    mutate: restoreDashboard,
    isPending: isRestoring,
    variables: restoringCode,
  } = useMutation({
    mutationFn: (code: string) => restoreDashboardApi(code),
    onSuccess: (data, code) => {
      queryClient.invalidateQueries({ queryKey: ["dashboards"] });
      // Drop (not just invalidate) any cached dashboard-detail query for this
      // code. If it was open before being deleted, react-query kept the old
      // "error" query result (data from before the delete, status: "error")
      // around instead of clearing it — invalidating alone re-marks it stale
      // but keeps that stale error result on the next mount, so the page
      // would flash "Failed to load dashboard" before the background
      // refetch resolves. Removing it outright forces a clean "pending"
      // state on first render instead.
      queryClient.removeQueries({ queryKey: ["dashboard_name", code] });
      if (data.restored_dashboard_code !== code) {
        queryClient.removeQueries({
          queryKey: ["dashboard_name", data.restored_dashboard_code],
        });
      }
      toast.success(
        data.renamed
          ? "Dashboard restored (renamed to avoid a naming conflict)."
          : "Dashboard restored.",
      );
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to restore dashboard.");
    },
  });

  const {
    mutate: permanentlyDeleteDashboard,
    isPending: isDeletingPermanently,
  } = useMutation({
    mutationFn: (code: string) => permanentlyDeleteDashboardApi(code),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dashboards", "recycle-bin"],
      });
      toast.success(data.message || "Dashboard permanently deleted.");
      setPendingDelete(null);
    },
    onError: (error: AxiosError) => {
      // 404 means it's no longer in the bin — most likely purged from
      // another tab between page load and this click, not a real failure.
      // Refresh the list so the stale row (and its now-invalid action)
      // disappears instead of leaving a dead "Delete permanently" button.
      if (error.response?.status === 404) {
        queryClient.invalidateQueries({
          queryKey: ["dashboards", "recycle-bin"],
        });
        toast.info("That dashboard was already permanently deleted.");
      } else {
        toast.error(error.message || "Failed to permanently delete dashboard.");
      }
      setPendingDelete(null);
    },
  });

  const dashboards = recycleBin?.dashboards ?? [];

  return (
    <div className="container-fluid px-4 px-lg-5 py-3 overflow-y-auto overflow-x-hidden">
      <div className="row my-3 align-items-center">
        <div className="col-12 d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-light"
            aria-label="Go back"
            title="Go back"
            onClick={() => navigate(-1)}
          >
            <i className="bi bi-arrow-left" />
          </button>
          <h3 className="me-2 mb-0 fw-bold text-primary">Recycle Bin</h3>
        </div>
      </div>

      <div className="card shadow-sm p-3">
        <Table striped hover responsive>
          <thead>
            <tr className="text-center">
              <th>Name</th>
              <th style={{ whiteSpace: "nowrap" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              [...Array(5).keys()].map((i) => (
                <tr key={i}>
                  <td className="placeholder-glow">
                    <span className="placeholder placeholder-sm w-100" />
                  </td>
                  <td className="placeholder-glow">
                    <span className="placeholder placeholder-sm w-100" />
                  </td>
                </tr>
              ))
            ) : dashboards.length > 0 ? (
              dashboards.map((dashboard) => (
                <tr key={dashboard.code}>
                  <td className="text-center">{dashboard.name}</td>
                  <td className="text-center">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-success me-2"
                      onClick={() => restoreDashboard(dashboard.code)}
                      disabled={isRestoring && restoringCode === dashboard.code}
                    >
                      {isRestoring && restoringCode === dashboard.code
                        ? "Restoring..."
                        : "Restore"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => setPendingDelete(dashboard)}
                      disabled={isDeletingPermanently}
                    >
                      Delete permanently
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={2} className="border-0">
                  <div className="d-flex justify-content-center p-3">
                    <NoDataFound />
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      <Modal
        show={!!pendingDelete}
        centered
        onHide={() =>
          isDeletingPermanently ? undefined : setPendingDelete(null)
        }
      >
        <Modal.Header closeButton={!isDeletingPermanently}>
          <Modal.Title>Delete permanently</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This cannot be undone. Do you want to permanently delete{" "}
          <strong>{pendingDelete?.name}</strong>? The dashboard and all its
          widgets will be lost.
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setPendingDelete(null)}
            disabled={isDeletingPermanently}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() =>
              pendingDelete && permanentlyDeleteDashboard(pendingDelete.code)
            }
            disabled={isDeletingPermanently}
          >
            {isDeletingPermanently ? "Deleting..." : "Delete permanently"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
